import * as React from 'react';
import * as api from 'app/shared/api';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Tabs } from 'antd';
import { getAccommodationProductsData, getAddOnsProductsData, getCourseProductsData, getSelectedProductType, getTransfersProductsData, getFeesProductsData, getCancellationFeesProductsData, getPackageProductsData } from "../selectors";
import {
    changeSelectedProductType,
    listAccommodationProductsData,
    listAddOnsProductsData,
    listCourseProductsData,
    listTransferssProductsData,
    listFeesProductsData,
    listPackageProductsData,
    filterData
} from 'app/pages/Products/actions';
import { Product } from 'app/shared/model/ProductBase';
import { AccommodationProduct, AddOnsProduct, CourseProduct, Fee, CancellationFee, PackageProduct, TransfersProduct } from 'app/shared/model';
import { getSelectedDivisionIdOrUndefined } from 'app/shared/division/selectedDivisionStorage';

export interface ProductsTabProps {

}

interface StateProps {
    selectedProductType: string;
    courseProducts?: CourseProduct[];
    accommodationProducts?: AccommodationProduct[];
    addOnsProducts?: AddOnsProduct[];
    transfersProducts?: TransfersProduct[];
    feeProducts?: Fee[];
    cancellationFeeProducts?: CancellationFee[];
    packageProducts?: PackageProduct[];
}

interface DispatchProps {
    changeSelectedProductType: (value: string, tabChanged: boolean) => void,
    onFilterData: (filteredData: Product[] | undefined) => void,
    onCourseData: (courseProducts: CourseProduct[]) => void,
    onAccommodationData: (accommodationProducts: AccommodationProduct[]) => void,
    onAddOnsData: (addOnsListData: AddOnsProduct[]) => void,
    onTransfersData: (transferListData: TransfersProduct[]) => void,
    onFeeProductsData?: (feeProducts: Fee[]) => void,

    onPackagesData?: (packagesListData: PackageProduct[]) => void
}

type InternalProps = StateProps & DispatchProps;

const getDivisionId = (): number | undefined => getSelectedDivisionIdOrUndefined();

const tabKeys = [
    { key: 'Accommodation', label: 'Accommodation' },
    { key: 'Courses', label: 'Courses' },
    { key: 'AddOns', label: 'Add Ons' },
    { key: 'Transfers', label: 'Transfers' },
    { key: 'Fees', label: 'Standard Fees' },
    { key: 'Packages', label: 'Packages' },
];

function ProductsTabs(props: InternalProps) {
    const [activeKey, setActiveKey] = React.useState<string>(props.selectedProductType || 'Accommodation');
    const [divisionId, setDivisionId] = React.useState(getDivisionId());
    const fetchStateRef = React.useRef<Record<string, { division: number | null; inFlight: boolean; loaded: boolean }>>({});
    const lastDivisionRef = React.useRef<number | undefined>(divisionId);

    const updateFetchState = React.useCallback((key: string, divisionKey: number | null, updates: Partial<{ inFlight: boolean; loaded: boolean; division: number | null }>) => {
        const current = fetchStateRef.current[key] ?? { division: null, inFlight: false, loaded: false };
        fetchStateRef.current[key] = {
            division: updates.division !== undefined ? updates.division : current.division,
            inFlight: updates.inFlight !== undefined ? updates.inFlight : current.inFlight,
            loaded: updates.loaded !== undefined ? updates.loaded : current.loaded
        };
    }, []);

    const shouldSkipFetch = React.useCallback((key: string, divisionKey: number | null) => {
        const meta = fetchStateRef.current[key];
        if (!meta) {
            return false;
        }
        if (meta.division === divisionKey && (meta.inFlight || meta.loaded)) {
            return true;
        }
        return false;
    }, []);

    const ensureFetch = React.useCallback((key: string, divisionKey: number | null, fetcher: (() => Promise<unknown>) | undefined) => {
        if (!fetcher) {
            return;
        }
        if (shouldSkipFetch(key, divisionKey)) {
            return;
        }
        updateFetchState(key, divisionKey, { division: divisionKey, inFlight: true, loaded: false });
        Promise.resolve(fetcher())
            .then(() => {
                updateFetchState(key, divisionKey, { loaded: true, division: divisionKey });
            })
            .catch(err => {
                console.error(`Failed to load ${key}`, err);
                updateFetchState(key, divisionKey, { loaded: false });
            })
            .finally(() => {
                updateFetchState(key, divisionKey, { inFlight: false });
            });
    }, [shouldSkipFetch, updateFetchState]);

    React.useEffect(() => {
        // When division changes, clear any existing filtered list so grids don't keep showing
        // the previous division's filteredData.
        if (lastDivisionRef.current !== divisionId) {
            lastDivisionRef.current = divisionId;
            props.onFilterData(undefined);
        }
    }, [divisionId, props.onFilterData]);

    React.useEffect(() => {
        const onDivisionChanged = () => {
            const current = getDivisionId();
            if (current !== divisionId) {
                setDivisionId(current);
            }
        };
        window.addEventListener('selectedDivisionChanged', onDivisionChanged);
        return () => window.removeEventListener('selectedDivisionChanged', onDivisionChanged);
    }, [divisionId]);

    React.useEffect(() => {
        switch (activeKey) {
            case "Courses":
                if (divisionId) {
                    ensureFetch("Courses", divisionId, () => api.getCourseProducts(divisionId).then(props.onCourseData));
                }
                break;
            case "AddOns":
                if (divisionId) {
                    ensureFetch("AddOns", divisionId, () => api.getAddonsProducts(divisionId).then(props.onAddOnsData));
                }
                break;
            case "Transfers":
                if (divisionId) {
                    ensureFetch("Transfers", divisionId, () => api.getTransferProducts(divisionId).then(props.onTransfersData));
                }
                break;
            case "Fees":
                if (divisionId) {
                    ensureFetch("Fees", divisionId, () => props.onFeeProductsData ? api.getAllFees().then(props.onFeeProductsData) : Promise.resolve());
                }
                break;
            case "Packages":
                if (divisionId) {
                    ensureFetch("Packages", divisionId, () => props.onPackagesData ? api.getPackageProducts(divisionId).then(props.onPackagesData) : Promise.resolve());
                }
                break;
            case "Accommodation":
            default:
                if (divisionId) {
                    ensureFetch("Accommodation", divisionId, () => api.getAccommodationProducts(divisionId).then(props.onAccommodationData));
                }
                break;
        }
    }, [
        activeKey,
        divisionId,
        props.onAccommodationData,
        props.onCourseData,
        props.onAddOnsData,
        props.onTransfersData,
        props.onFeeProductsData,
        props.packageProducts?.length,
        props.addOnsProducts?.length,
        props.transfersProducts?.length,
        props.courseProducts?.length,
        props.feeProducts?.length,
        props.accommodationProducts?.length,
        props.onPackagesData,
        ensureFetch
    ]);

    React.useEffect(() => {
        if (props.selectedProductType && props.selectedProductType !== activeKey) {
            setActiveKey(props.selectedProductType);
        }
    }, [props.selectedProductType]);

    const handleTabChange = (key: string) => {
        setActiveKey(key);
        props.changeSelectedProductType(key, true);
        props.onFilterData(undefined);
    };

    return (
        <Tabs
            activeKey={activeKey}
            onChange={handleTabChange}
            type="card"
            items={tabKeys}
            tabBarGutter={24}
            style={{ marginBottom: 16 }}
        />
    );
}

const mapStateToProps = (state: any, ownProps: ProductsTabProps): StateProps => ({
    selectedProductType: getSelectedProductType(state),
    courseProducts: getCourseProductsData(state),
    accommodationProducts: getAccommodationProductsData(state),
    addOnsProducts: getAddOnsProductsData(state),
    transfersProducts: getTransfersProductsData(state),
    feeProducts: getFeesProductsData(state),
    cancellationFeeProducts: getCancellationFeesProductsData(state),
    packageProducts: getPackageProductsData(state)
});

const mapDispatchToProps = (dispatch: Dispatch<any>): DispatchProps => ({
    changeSelectedProductType: (productType: string, tabChanged: boolean) => dispatch(changeSelectedProductType(productType, tabChanged)),
    onFilterData: (filteredData: Product[] | undefined) => dispatch(filterData(filteredData)),
    onCourseData: (courseProducts: CourseProduct[]) => dispatch(listCourseProductsData(courseProducts)),
    onAccommodationData: (accommodationProducts: AccommodationProduct[]) => dispatch(listAccommodationProductsData(accommodationProducts)),
    onAddOnsData: (addOnsListData: AddOnsProduct[]) => dispatch(listAddOnsProductsData(addOnsListData)),
    onTransfersData: (transfersListData: TransfersProduct[]) => dispatch(listTransferssProductsData(transfersListData)),
    onFeeProductsData: (feeProducts: Fee[]) => dispatch(listFeesProductsData(feeProducts)),
    onPackagesData: (packagesListData: PackageProduct[]) => dispatch(listPackageProductsData(packagesListData))
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductsTabs);